import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './category-pages.api';

import type {
  CreateWebsiteCategoryPageRequest,
  UpdateWebsiteCategoryPageRequest,
  WebsiteCategoryPageListQuery,
} from '@utazzo/shared';

const QK = {
  list: (query: WebsiteCategoryPageListQuery) => ['category-pages', 'list', query] as const,
  detail: (id: string) => ['category-pages', 'detail', id] as const,
  destinationOptions: (id: string) => ['category-pages', 'destination-options', id] as const,
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};

export const useCategoryPagesList = (query: WebsiteCategoryPageListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.list(query),
    staleTime: 30_000,
  });

export const useCategoryPageDetail = (id: string | undefined) =>
  useQuery({
    queryKey: id ? QK.detail(id) : ['category-pages', 'detail', 'none'],
    queryFn: () => api.get(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

/**
 * Destination tiles bound to this category, in the order the website
 * renders them. Backs the "Sorting" tab; unavailable on an unsaved page
 * (no id yet), which is why the tab tells the operator to save first.
 */
export const useCategoryPageDestinationOptions = (id: string | undefined) =>
  useQuery({
    queryKey: id ? QK.destinationOptions(id) : ['category-pages', 'destination-options', 'none'],
    queryFn: () => api.listDestinationOptions(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export const useCreateCategoryPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWebsiteCategoryPageRequest) => api.create(payload),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['category-pages', 'list'] });
      toast.success(`Created ${p.title}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create category page'),
  });
};

export const useUpdateCategoryPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWebsiteCategoryPageRequest }) =>
      api.update(id, payload),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['category-pages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(p.id) });
      toast.success('Category page saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save category page'),
  });
};

export const useDeleteCategoryPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['category-pages', 'list'] });
      toast.success('Category page deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete category page'),
  });
};

export const usePublishCategoryPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.publish(id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['category-pages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(p.id) });
      toast.success('Published');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to publish'),
  });
};

export const useUnpublishCategoryPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.unpublish(id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['category-pages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(p.id) });
      toast.success('Unpublished');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to unpublish'),
  });
};
